"use client";

import React, { useRef, useState, useEffect } from "react";
import Lottie, { LottieRefCurrentProps } from "lottie-react";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { SectionContainer } from "./layouts";

const MadeForFrequentTravelers: React.FC<{ className?: string }> = ({
  className,
}) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const lottieRef = useRef<LottieRefCurrentProps>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [animationData, setAnimationData] = useState<object | null>(null);

  useEffect(() => {
    import(
      "@/public/animations/Gondola_BringingPointsTogether_Lottie.json"
    ).then((mod) => setAnimationData(mod.default));
  }, []);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!lottieRef.current) return;
    if (isVisible) {
      lottieRef.current.goToAndPlay(0);
    } else {
      lottieRef.current.goToAndStop(0);
    }
  }, [isVisible]);

  return (
    <SectionContainer
      background="transparent"
      maxWidth="6xl"
      className={className}
    >
      <div
        ref={sectionRef}
        className="inline-flex w-full max-w-[1106px] flex-col items-center justify-start gap-10"
      >
        {/* Title */}
        <div className="w-full text-balance text-center font-figtree text-4xl font-semibold leading-9 text-foreground sm:text-6xl sm:leading-none">
          Made for frequent travelers
        </div>

        {/* Two Cards Layout */}
        <div className="flex w-full flex-col items-center justify-start gap-8 sm:flex-row sm:items-stretch">
          <div className="flex w-full flex-1 flex-col overflow-hidden rounded-xl bg-landing-cream">
            <div className="p-6 text-left font-figtree text-lg font-normal leading-7 text-pure-black md:p-8 md:text-3xl md:leading-8">
              Gondola brings your points, loyalty info, and trips into one
              place.
            </div>
            <div
              className="relative w-full overflow-hidden"
              style={{ aspectRatio: "1790/1600" }}
            >
              {animationData ? (
                <Lottie
                  lottieRef={lottieRef}
                  animationData={animationData}
                  loop={true}
                  autoplay={false}
                  className="h-full w-full"
                />
              ) : (
                <div className="h-full w-full" />
              )}
            </div>
          </div>
          <div className="flex w-full flex-1 flex-col overflow-hidden rounded-xl bg-landing-cream">
            <div className="p-6 text-left font-figtree text-lg font-normal leading-7 text-pure-black md:p-8 md:text-3xl md:leading-8">
              Find the right hotel faster, earn more, and save automatically.
            </div>
            <div
              className="relative w-full overflow-hidden"
              style={{ aspectRatio: "1790/1600" }}
            >
              <DotLottieReact
                src="/animations/Gondola_BookingAnimation_Lottie.lottie"
                loop
                autoplay
                className="h-full w-full"
              />
            </div>
          </div>
        </div>
      </div>
    </SectionContainer>
  );
};

export default MadeForFrequentTravelers;
